import React, { lazy, ComponentType, Suspense, startTransition } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import ErrorFallback from '../components/ErrorFallback';
import Logger from '../services/logger';

/**
 * Performance-optimized lazy loading utility
 * Provides error boundaries, loading states, and retry mechanisms
 */

interface LazyLoadingOptions {
  fallback?: ComponentType;
  retryDelay?: number;
  maxRetries?: number;
  onError?: (error: Error) => void;
  onLoad?: () => void;
}

/**
 * Creates a lazy-loaded component with error boundary and retry logic
 */
export function createLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: LazyLoadingOptions = {}
): ComponentType<any> {
  const {
    fallback: FallbackComponent,
    retryDelay = 1000,
    maxRetries = 3,
    onError,
    onLoad
  } = options;

  // Wrap import function with ChunkLoadError handling
  const wrappedImportFn = async () => {
    try {
      return await importFn();
    } catch (error: any) {
      // Handle ChunkLoadError specifically
      if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Failed to fetch dynamically imported module')) {
        // Clear cache and reload page for ChunkLoadError
        Logger.warn('ChunkLoadError detected, reloading page', { error: error.message });
        // Force a hard reload to clear stale chunks
        window.location.reload();
        throw error; // Still throw to trigger retry logic
      }
      throw error;
    }
  };

  const LazyComponent = lazy(wrappedImportFn);

  // Enhanced error boundary for lazy components with Suspense
  const LazyErrorBoundary = (props: any) => (
    <ErrorBoundary
      fallback={FallbackComponent ? <FallbackComponent /> : undefined}
      onError={(error, errorInfo) => {
        // Handle ChunkLoadError with page reload
        if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Failed to fetch dynamically imported module')) {
          Logger.warn('ChunkLoadError in ErrorBoundary, reloading page', { error: error.message });
          setTimeout(() => window.location.reload(), 1000);
          return;
        }
        Logger.error('Lazy component failed to load', error, errorInfo);
        onError?.(error);
      }}
      resetOnPropsChange={true}
    >
      <Suspense fallback={FallbackComponent ? <FallbackComponent /> : <div>Loading...</div>}>
        <LazyComponent {...props} />
      </Suspense>
    </ErrorBoundary>
  );

  // Add retry logic
  LazyErrorBoundary.retry = async () => {
    let retries = 0;
    while (retries < maxRetries) {
      try {
        await importFn();
        onLoad?.();
        return;
      } catch (error) {
        retries++;
        if (retries >= maxRetries) {
          Logger.error('Max retries exceeded for lazy component', error as Error);
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, retryDelay * retries));
      }
    }
  };

  return LazyErrorBoundary;
}

/**
 * Preloads a component for better performance
 */
export function preloadComponent(importFn: () => Promise<any>) {
  return importFn().catch(error => {
    Logger.warn('Failed to preload component', error);
  });
}

/**
 * Creates a loading component with performance metrics
 */
export function createLoadingComponent(componentName: string) {
  return () => {
    const startTime = performance.now();
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{ color: '#666', fontSize: '14px' }}>
          Loading {componentName}...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  };
}

/**
 * Performance monitoring for lazy components
 */
export class LazyLoadingMonitor {
  private static instance: LazyLoadingMonitor;
  private metrics: Map<string, { loadTime: number; retries: number; errors: number }> = new Map();

  static getInstance(): LazyLoadingMonitor {
    if (!LazyLoadingMonitor.instance) {
      LazyLoadingMonitor.instance = new LazyLoadingMonitor();
    }
    return LazyLoadingMonitor.instance;
  }

  recordLoad(componentName: string, loadTime: number) {
    const existing = this.metrics.get(componentName) || { loadTime: 0, retries: 0, errors: 0 };
    this.metrics.set(componentName, {
      ...existing,
      loadTime: Math.max(existing.loadTime, loadTime)
    });
    
    Logger.info(`Lazy component loaded: ${componentName}`, { loadTime });
  }

  recordRetry(componentName: string) {
    const existing = this.metrics.get(componentName) || { loadTime: 0, retries: 0, errors: 0 };
    this.metrics.set(componentName, {
      ...existing,
      retries: existing.retries + 1
    });
  }

  recordError(componentName: string) {
    const existing = this.metrics.get(componentName) || { loadTime: 0, retries: 0, errors: 0 };
    this.metrics.set(componentName, {
      ...existing,
      errors: existing.errors + 1
    });
  }

  getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  getPerformanceReport() {
    const metrics = this.getMetrics();
    const totalComponents = Object.keys(metrics).length;
    const avgLoadTime = Object.values(metrics).reduce((sum, m) => sum + m.loadTime, 0) / totalComponents;
    const totalRetries = Object.values(metrics).reduce((sum, m) => sum + m.retries, 0);
    const totalErrors = Object.values(metrics).reduce((sum, m) => sum + m.errors, 0);

    return {
      totalComponents,
      avgLoadTime: Math.round(avgLoadTime),
      totalRetries,
      totalErrors,
      successRate: totalComponents > 0 ? ((totalComponents - totalErrors) / totalComponents * 100).toFixed(1) : '100'
    };
  }
}

export default LazyLoadingMonitor;
